import { DateTruncExpression } from '@fabric-ui/components/dist';
import { SortType } from '@fabric-ui/components/Table';
import { Theme } from '@mui/material';
import {
  getDateBucketFromExpression,
  getFieldTitleByExpression,
  getFieldTypeByExpression,
  isDateTruncExpression,
} from '@views/CustomDashboards/CustomDashboardsUtils/expressionUtils';
import { Measurement } from '../../../../../Measurements/types';
import { hasTimeBucket } from '../../../../EditCustomDashboard/TimeBucketBreakdown/utils';
import { getFieldType } from '../../../../getFieldType';
import { Field, QueryObjectMetric, SortBy, SortDir } from '../../../../types';

export const getFieldName = (rowDim: Field) => {
  if (hasTimeBucket(rowDim.name)) {
    return { fieldName: rowDim.name };
  }

  if (rowDim.expression && isDateTruncExpression(rowDim.expression)) {
    return { fieldName: getDateBucketFromExpression(rowDim.expression as DateTruncExpression) };
  }

  return {};
};

export const getMetricHeadCells = ({
  metrics,
  sortingRow,
  getDisplayName,
  commonSX,
  colDim,
  fieldTypeMap,
  measurements,
}: {
  metrics: QueryObjectMetric[];
  sortingRow: SortBy[];
  getDisplayName: (fieldName?: string | null | undefined | number) => string | null | undefined | number;
  commonSX: Record<string, any>;
  colDim: Field | undefined;
  fieldTypeMap: Record<string, string>;
  measurements: Measurement[];
}) =>
  metrics.map(({ name }) => {
    const shouldSortByNonGroupedMetric = sortingRow[0]?.name === name && !sortingRow[0]?.path;
    return {
      id: name,
      label: getDisplayName(name),
      sx: commonSX,
      getValue: (row: any, id: string, options: { group: string }) => (colDim ? row[options.group]?.[id] : row[id]),
      type: getFieldType({ entityFieldKey: name, fieldTypeMap, measurements }),
      ...(shouldSortByNonGroupedMetric && { sort: sortingRow[0].dir === SortDir.ASC ? SortType.ASC : SortType.DESC }),
    };
  });

interface GetRowDimHeadCellProps {
  rowDim: Field;
  getDisplayName: (fieldName?: string | null | undefined | number) => string | null | undefined | number;
  sortingRow: SortBy[];
  commonSX: Record<string, any>;
  theme: Theme;
  fieldTypeMap: Record<string, string>;
  measurements: Measurement[];
}

export const getRowDimHeadCell = ({
  rowDim,
  getDisplayName,
  sortingRow,
  commonSX,
  theme,
  fieldTypeMap,
  measurements,
}: GetRowDimHeadCellProps) => {
  const displayName = getDisplayName(rowDim.name);
  const shouldSortByRowDim = sortingRow[0]?.name === rowDim.name && !sortingRow[0]?.path;

  return {
    id: rowDim.name,
    label: rowDim.expression ? getFieldTitleByExpression({ expression: rowDim.expression, displayName }) : displayName,
    sx: { ...commonSX, position: 'sticky', left: 0 },
    rowStyle: () => ({
      position: 'sticky',
      left: 0,
      background: theme.palette.colors.neutrals[150],
      borderRight: `1px solid ${theme.palette.colors.neutrals[350]}`,
      zIndex: 1,
    }),
    type: rowDim.expression
      ? getFieldTypeByExpression(rowDim.expression)
      : getFieldType({ entityFieldKey: rowDim.name, fieldTypeMap, measurements }),
    ...getFieldName(rowDim),
    ...(shouldSortByRowDim && { sort: sortingRow[0].dir === SortDir.ASC ? SortType.ASC : SortType.DESC }),
  };
};
